import { VcardData } from "@/types/qr";
import { isClient } from "./browser";

const imageToBase64 = (url) =>
  fetch(url)
    .then((response) => response.blob())
    .then(
      (blob) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        })
    );

const formatVCardFileName = (fullName: string): string => {
  const normalizedName = fullName
    .normalize("NFD")
    .toLowerCase()
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

  const pascalCasedName = normalizedName
    .split(/\s+/)
    .map((word) => {
      if (!word) return "";
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join("");

  return `vcard-${pascalCasedName}.vcf`;
};

export const generateVCard = async (vcardData: VcardData) => {
  const {
    fullName,
    mobilePhone,
    landlinePhone,
    fax,
    email,
    addressObj,
    website,
    companyName,
    profession,
    logoUrl,
  } = vcardData;
  let imageEncoded;

  logoUrl &&
    (await imageToBase64(logoUrl).then((dataUrl: string) => {
      imageEncoded = dataUrl.split(",")[1];
    }));

  const makeVCardInfo = (fullName) => `FN;CHARSET=UTF-8:${fullName}`;
  const makeVCardName = (fullName) => `N;CHARSET=UTF-8:;${fullName};;;`;
  const makeVCardOrg = (companyName) => `ORG;CHARSET=UTF-8:${companyName || ""}`;
  const makeVCardTitle = (profession) => `TITLE;CHARSET=UTF-8:${profession || ""}`;
  const makeVCardPhoto = () => `PHOTO;ENCODING=b:${imageEncoded || ""}`;
  const makeVCardMobilePhone = (mobilePhone) => `TEL;TYPE=CELL:${mobilePhone || ""}`;
  const makeVCardLandlinePhone = (landlinePhone) => `TEL;TYPE=WORK:${landlinePhone || ""}`;
  const makeVCardFaxPhone = (fax) => `TEL;TYPE=WORK,FAX:${fax || ""}`;
  const makeVCardAddress = (addressObj) => {
    if (!addressObj) return "";
    const { address, numeration, city, zipCode, state, country } = addressObj;
    const street = `${address}${numeration ? ` ${numeration}` : ""}`.trim();
    const adrValue = ["", "", street, city, state, zipCode, country].join(";").trim();
    return `ADR;TYPE=WORK;CHARSET=UTF-8:${adrValue}`;
  };
  const makeVCardEmail = (email) => `EMAIL:${email || ""}`;
  const makeVCardWebsite = (website) => `URL:${website || ""}`;
  const makeVCardTimeStamp = () => `REV:${new Date().toISOString()}`;

  const lines = [
    "BEGIN:VCARD",
    "VERSION:3.0",
    makeVCardPhoto(),
    makeVCardInfo(fullName),
    makeVCardName(fullName),
    makeVCardOrg(companyName),
    makeVCardTitle(profession),
    makeVCardMobilePhone(mobilePhone),
    makeVCardLandlinePhone(landlinePhone),
    makeVCardFaxPhone(fax),
    makeVCardAddress(addressObj),
    makeVCardEmail(email),
    makeVCardWebsite(website),
    makeVCardTimeStamp(),
    "END:VCARD",
  ].filter((line) => line && !line.endsWith(":"));
  const vcard = lines.join("\r\n") + "\r\n";

  const blob = new Blob([vcard.trim()], { type: "text/x-vcard" });
  const downloadUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = downloadUrl;
  a.download = formatVCardFileName(fullName);
  document.body.appendChild(a);
  a.click();
};

export const downloadTypeOfData = (data: string | ArrayBuffer, type: string, fileName: string) => {
  try {
    const blob = new Blob([data], {
      type,
    });

    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName;

    link.click();

    window.URL.revokeObjectURL(link.href);
  } catch (err) {
    console.error("Error downloading the asset:", err);
  }
};

export const downloadXLSX = async (data: string, label: string) => {
  const fileName = `${label}.xlsx`;
  const headerType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

  const response = await fetch(`data:application/octet-stream;base64,${data}`);
  const binaryData = await response.arrayBuffer();

  downloadTypeOfData(binaryData, headerType, fileName);
};

export const downloadCSV = async (data: string, label: string) => {
  const fileName = `${label}.csv`;
  const headerType = "text/csv";

  downloadTypeOfData(data, headerType, fileName);
};

export const downloadPDF = async (pdf: Uint8Array, fileName: string) => {
  if (isClient()) {
    downloadTypeOfData(pdf.buffer as any, "application/pdf", fileName);
  }
};
